/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  styled,
  Button,
  Tooltip,
  Checkbox,
  MenuItem,
  Select,
} from '@mui/material';
import FormInput from 'UI/Form/FormInput';
import FormNumberInput from 'UI/Form/FormNumberInput';
import { SwitchButton } from '@UI/SwitchButton';
import { Controller, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useSendFinancialReportNowMutation } from '@Store/api/customersSlice';

interface ISurroundingsSelection {
  evChargePoints?: number | string | null;
  disabledBays?: number | string | null;
  heightRestriction?: number | string | null;
  hasLifts?: boolean;
  hasToilets?: boolean;
  freeText?: string | null;
  surroundingsInfo?: string | null;
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

// Builds the readonly "Nearby Landmarks" text from the individual template
// selections - any blank number field or unchecked checkbox is simply left
// out of the generated text rather than shown as empty/false.
function buildSurroundingsInfo(selection: ISurroundingsSelection): string {
  const parts: string[] = [];

  if (hasValue(selection.evChargePoints)) {
    parts.push(`EV Charge Points: ${selection.evChargePoints}`);
  }

  if (hasValue(selection.disabledBays)) {
    parts.push(`Disabled Bays: ${selection.disabledBays}`);
  }

  if (hasValue(selection.heightRestriction)) {
    parts.push(`Height Restriction: ${selection.heightRestriction}m`);
  }

  if (selection.hasLifts) {
    parts.push('Lifts available');
  }

  if (selection.hasToilets) {
    parts.push('Toilets available');
  }

  if (hasValue(selection.freeText)) {
    parts.push(selection.freeText as string);
  }

  return parts.join('\n');
}

const RemoveIcon = () => (
  <svg
    fill='none'
    height='12'
    viewBox='0 0 12 12'
    width='12'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M11.7472 10.5352C11.9081 10.6962 11.9986 10.9145 11.9986 11.1422C11.9986 11.3698 11.9081 11.5881 11.7472 11.7491C11.5862 11.91 11.3679 12.0005 11.1403 12.0005C10.9126 12.0005 10.6943 11.91 10.5333 11.7491L6 7.21431L1.46523 11.7476C1.30427 11.9086 1.08595 11.999 0.858315 11.999C0.630676 11.999 0.41236 11.9086 0.251395 11.7476C0.0904294 11.5867 3.39209e-09 11.3684 0 11.1407C-3.39209e-09 10.9131 0.0904295 10.6948 0.251395 10.5338L4.78616 6.00047L0.252822 1.4657C0.0918573 1.30474 0.00142808 1.08642 0.00142808 0.858785C0.00142808 0.631145 0.0918573 0.41283 0.252822 0.251864C0.413788 0.0908992 0.632104 0.000469766 0.859743 0.000469763C1.08738 0.000469761 1.3057 0.0908992 1.46666 0.251864L6 4.78663L10.5348 0.25115C10.6957 0.090185 10.914 -0.000244144 11.1417 -0.000244141C11.3693 -0.000244137 11.5876 0.090185 11.7486 0.25115C11.9096 0.412116 12 0.630431 12 0.85807C12 1.08571 11.9096 1.30403 11.7486 1.46499L7.21384 6.00047L11.7472 10.5352Z'
      fill='white'
    />
  </svg>
);

const RemoveButton = styled(Button)(() => ({
  backgroundColor: '#a91d54',
  padding: '5px 10px !important',
  minHeight: 32,
  maxHeight: 32,
  height: 32,
  borderRadius: 10,
  color: '#FFF',

  svg: {
    marginRight: 10,
  },

  textTransform: 'capitalize',

  '&:hover': { backgroundColor: '#a91d54' },
}));

const FieldTitleContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FieldContainer = styled(Box)`
  margin-top: 15px;
  margin-bottom: 15px;
`;

const FormContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: 0px;

  & .MuiFormHelperText-root {
    color: red !important;
  }
`;

const StyledTypography = styled(Typography)`
  font-size: 16px;
  font-weight: 700;
  margin-left: 20px !important;
  margin-bottom: 20px;
`;

const SelectionId = styled(Typography)`
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ModalContentContainer = styled(Box)`
  overflow: auto;
  padding: 20px;
  height: calc(100% - 100px);
`;

const VATTitle = styled(Typography)`
  color: #181818;

  font-size: 16px;
  font-style: normal;
  font-weight: 700;
`;

const TemplateGroupContainer = styled(Box)`
  margin-top: 30px;
  padding: 15px 20px;
  border: 1px solid rgba(24, 24, 24, 0.1);
  border-radius: 15px;
`;

const TemplateGroupTitle = styled(Typography)`
  color: #181818;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 15px;
`;

const TemplateFieldLabel = styled(Typography)`
  color: #181818;
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
`;

const templateNumberInputSx = {
  width: 70,
  minHeight: 'unset',
  '& .MuiOutlinedInput-root': {
    padding: 0,
    minHeight: 'unset',
  },
  '& .MuiInputBase-input': {
    fontSize: 13,
    padding: '6px 8px',
    textAlign: 'center',
  },
};

function isGreaterOrLess(a: number, b: number) {
  return !Number.isNaN(a) && !Number.isNaN(b) && a !== b;
}

function CustomerFormModal({
  methods,
  fields,
  handleFormSubmit,
  onRemovePolygon,
  editing = false,
  customerId,
  handleSelectionClick,
  onVATToggleChange,
  reminderFee,
  graceTime,
  convenienceFee,
  controlledFields,
  vatNumber,
  handleAppletToggleChange,
  handleVisibleOnAppToggleChange,
  handleCancellationToggleChange,
}: any) {
  const handleVATToggleChange = (index: number, isChecked: boolean) => {
    onVATToggleChange(index, isChecked);
  };

  const { setValue, watch, formState } = methods;

  const [sendFinancialReportNow, { isLoading: isSendingReport }] =
    useSendFinancialReportNowMutation();

  const handleSendReportNow = async () => {
    if (!customerId) {
      return;
    }

    try {
      const result = await sendFinancialReportNow(customerId).unwrap();

      if (result.sent) {
        toast.success(result.message);
      } else {
        toast.warning(result.message);
      }
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || 'Failed to send report';

      toast.error(message);
    }
  };

  useEffect(() => {
    if (!vatNumber) {
      // If 'vatNumber' is cleared, reset the dependent fields
      setValue('companyAddress', null);
      setValue('companyCity', null);
      setValue('companyRegion', null);
      setValue('companyPostCode', null);
    }
  }, [vatNumber, setValue]);

  const watchedSelections = watch('selections');
  const sendFinancialEmailAutomatically = watch(
    'sendFinancialEmailAutomatically'
  );

  useEffect(() => {
    if (!sendFinancialEmailAutomatically) {
      setValue('emailScheduleDayOfMonth', null);
    }
  }, [sendFinancialEmailAutomatically, setValue]);

  useEffect(() => {
    (watchedSelections || []).forEach((selection: any, index: number) => {
      // Only regenerate once the user has actually used one of the template
      // controls for this selection - otherwise, on an existing customer
      // being edited, this would immediately blank out whatever text was
      // saved before this feature existed (those 6 fields can't be
      // reconstructed from the saved string, so they start empty).
      const dirtySelection = formState.dirtyFields?.selections?.[index];
      const templateTouched = Boolean(
        dirtySelection?.evChargePoints ||
          dirtySelection?.disabledBays ||
          dirtySelection?.heightRestriction ||
          dirtySelection?.hasLifts ||
          dirtySelection?.hasToilets ||
          dirtySelection?.freeText
      );

      if (!templateTouched) {
        return;
      }

      const generated = buildSurroundingsInfo(selection);
      
      if (selection.surroundingsInfo !== generated) {
        setValue(`selections.${index}.surroundingsInfo`, generated, {
          shouldDirty: true,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedSelections), formState.dirtyFields, setValue]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 40,
        right: 112,
        height: 'calc(100vh - 200px)',
        width: 460,
        bgcolor: '#FFF',
        borderRadius: '30px',
        boxShadow: 24,
        paddingTop: 4,
        paddingBottom: 4,
        border: 'none',
        outline: 'none',
        zIndex: 9998,
      }}
    >
      <StyledTypography variant='body2'>Customer Form</StyledTypography>
      <ModalContentContainer>
        <FormProvider {...methods}>
          <FormContainer
            component={'form'}
            id='customer-form'
            onSubmit={(e) => {
              methods.handleSubmit(handleFormSubmit, () => {
                toast.error('One or more fields are not filled correctly');
              })(e);
            }}
          >
            <FormInput label='Customer Name' name={`name`} />
            <div style={{ marginTop: 30 }}>
              <FormInput label='Email' name={`companyEmail`} />
            </div>
            <div
              style={{
                marginTop: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Controller
                  control={methods.control}
                  name='sendFinancialEmailAutomatically'
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  )}
                />
                <Typography variant='body2'>
                  Send Financial Email Automatically
                </Typography>
              </div>
              {editing ? (
                <Tooltip title="Send last month’s payment report to this customer now">
                  <span>
                    <Button
                      disabled={isSendingReport}
                      size='small'
                      variant='outlined'
                      onClick={handleSendReportNow}
                    >
                      {isSendingReport ? 'Sending...' : 'Send Now'}
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </div>
            {sendFinancialEmailAutomatically ? (
              <div style={{ marginTop: 15 }}>
                <FormNumberInput
                  decimalScale={0}
                  label='Email Schedule (Day of Month)'
                  name='emailScheduleDayOfMonth'
                />
              </div>
            ) : null}
            <div style={{ marginTop: 30 }}>
              <FormInput label='VAT Number' name={`vatNumber`} />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormNumberInput
                decimalScale={4}
                label='Reminder Fee'
                name={`defaultReminderFee`}
              />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormNumberInput
                decimalScale={4}
                label='Convenience Fee'
                name={`defaultConvenienceFee`}
              />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormInput label='Merchand ID' name={`merchantId`} />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormNumberInput
                decimalScale={4}
                label='Grace Time (minutes)'
                name={'defaultGraceTime'}
              />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormInput
                disabled={!vatNumber}
                label='Address'
                name={`companyAddress`}
              />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormInput
                disabled={!vatNumber}
                label='City'
                name={`companyCity`}
              />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormInput
                disabled={!vatNumber}
                label='Region'
                name={`companyRegion`}
              />
            </div>
            <div style={{ marginTop: 30 }}>
              <FormInput
                disabled={!vatNumber}
                label='Post Code'
                name={`companyPostCode`}
              />
            </div>
            {fields.length > 0 && <Divider sx={{ marginTop: '30px' }} />}
            {fields.map((field: any, index: number) => {
              return (
                <FieldContainer
                  id={`selection-${index + 1}`}
                  key={field.id || field.front_id}
                >
                  <FieldTitleContainer>
                    <SelectionId onClick={() => handleSelectionClick(index)}>
                      {`Selection #${index + 1}`}
                    </SelectionId>
                    <RemoveButton onClick={() => onRemovePolygon(index)}>
                      <RemoveIcon />
                      Remove
                    </RemoveButton>
                  </FieldTitleContainer>
                  <div style={{ marginTop: 30 }}>
                    <FormInput
                      label='Subarea Name'
                      name={`selections.${index}.subareaName`}
                    />
                  </div>
                  <div style={{ marginTop: 30 }}>
                    <FormInput
                      label='Subarea ID'
                      name={`selections.${index}.subAreaId`}
                    />
                  </div>
                  <div style={{ marginTop: 30 }}>
                    <FormInput
                      label='Public Area Id'
                      name={`selections.${index}.publicId`}
                    />
                  </div>
                  <div style={{ marginTop: 30 }}>
                    <FormInput
                      label='Config ID'
                      name={`selections.${index}.configId`}
                    />
                  </div>
                  <div style={{ marginTop: 30 }}>
                    <FormNumberInput
                      decimalScale={0}
                      label='Default Time (minutes)'
                      name={`selections.${index}.defaultTime`}
                    />
                  </div>
                  <div style={{ marginTop: 30 }}>
                    <FormNumberInput
                      decimalScale={4}
                      label='Grace Time (minutes)'
                      name={`selections.${index}.graceTime`}
                      sx={
                        isGreaterOrLess(
                          graceTime,
                          controlledFields[index].graceTime
                        )
                          ? {
                              '& .MuiOutlinedInput-root': {
                                '&.MuiInputBase-root fieldset': {
                                  borderColor: 'orange',
                                },
                              },
                              '& .MuiInputLabel-outlined': {
                                color: 'orange',
                              },
                            }
                          : undefined
                      }
                    />
                  </div>
                  <div style={{ marginTop: 30 }}>
                    <FormNumberInput
                      decimalScale={4}
                      label='Reminder Fee'
                      name={`selections.${index}.reminderFee`}
                      sx={
                        isGreaterOrLess(
                          reminderFee,
                          controlledFields[index].reminderFee
                        )
                          ? {
                              '& .MuiOutlinedInput-root': {
                                '&.MuiInputBase-root fieldset': {
                                  borderColor: 'orange',
                                },
                              },
                              '& .MuiInputLabel-outlined': {
                                color: 'orange',
                              },
                            }
                          : undefined
                      }
                    />
                  </div>
                  <div style={{ marginTop: 30 }}>
                    <FormNumberInput
                      decimalScale={4}
                      label='Convenience Fee'
                      name={`selections.${index}.convenienceFee`}
                      sx={
                        isGreaterOrLess(
                          convenienceFee,
                          controlledFields[index].convenienceFee
                        )
                          ? {
                              '& .MuiOutlinedInput-root': {
                                '&.MuiInputBase-root fieldset': {
                                  borderColor: 'orange',
                                },
                              },
                              '& .MuiInputLabel-outlined': {
                                color: 'orange',
                              },
                            }
                          : undefined
                      }
                    />
                  </div>
                      <div style={{ marginTop: 12, width: '100%' }}>
                          <TemplateFieldLabel>Customer Page Options</TemplateFieldLabel>
                          <Controller
                              control={methods.control}
                              name={`selections.${index}.tariffDisplayMode`}
                              render={({ field }) => (
                                  <Select
                                      fullWidth
                                      size='small'
                                      MenuProps={{ sx: { zIndex: 10000 } }}
                                      value={Number(field.value ?? 2)}
                                      onChange={(event) => field.onChange(Number(event.target.value))}
                                  >
                                      <MenuItem value={0}>Tariff and Time Selector (both)</MenuItem>
                                      <MenuItem value={1}>Tariff only</MenuItem>
                                      <MenuItem value={2}>Time Selector wheel only</MenuItem>
                                  </Select>
                              )}
                          />
                      </div>
                  <TemplateGroupContainer>
                    <TemplateGroupTitle>Surroundings Info</TemplateGroupTitle>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'baseline',
                        gap: 10,
                      }}
                    >
                      <TemplateFieldLabel>EV Charge Points</TemplateFieldLabel>
                      <FormNumberInput
                        decimalScale={0}
                        name={`selections.${index}.evChargePoints`}
                        sx={templateNumberInputSx}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 2,
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'baseline',
                        gap: 10,
                      }}
                    >
                      <TemplateFieldLabel>Disabled Bays</TemplateFieldLabel>
                      <FormNumberInput
                        decimalScale={0}
                        name={`selections.${index}.disabledBays`}
                        sx={templateNumberInputSx}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 2,
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'baseline',
                        gap: 10,
                      }}
                    >
                      <TemplateFieldLabel>Height Restriction</TemplateFieldLabel>
                      <FormNumberInput
                        decimalScale={2}
                        name={`selections.${index}.heightRestriction`}
                        suffix='m'
                        sx={templateNumberInputSx}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <TemplateFieldLabel>Lift Available</TemplateFieldLabel>
                      <Controller
                        control={methods.control}
                        name={`selections.${index}.hasLifts`}
                        render={({ field }) => (
                          <Checkbox
                            checked={Boolean(field.value)}
                            size='small'
                            onChange={(event) => field.onChange(event.target.checked)}
                          />
                        )}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <TemplateFieldLabel>Toilet Available</TemplateFieldLabel>
                      <Controller
                        control={methods.control}
                        name={`selections.${index}.hasToilets`}
                        render={({ field }) => (
                          <Checkbox
                            checked={Boolean(field.value)}
                            size='small'
                            onChange={(event) => field.onChange(event.target.checked)}
                          />
                        )}
                      />
                    </div>
                    <div style={{ marginTop: 8, width: '100%' }}>
                      <FormInput
                        autoHeight
                        multiline
                        rows={2}
                        label='Free Text'
                        name={`selections.${index}.freeText`}
                      />
                    </div>
                    <div style={{ marginTop: 8, width: '100%' }}>
                      <FormInput
                        disabled
                        autoHeight
                        multiline
                        rows={4}
                        label='Nearby Landmarks (auto-generated)'
                        name={`selections.${index}.surroundingsInfo`}
                      />
                    </div>
                  </TemplateGroupContainer>                 

                  <div
                    style={{
                      marginTop: 20,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <VATTitle>Visible In App</VATTitle>
                    <SwitchButton
                      checked={field.isVisibleOnAppMap}
                      sx={{ m: 1 }}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleVisibleOnAppToggleChange(index, event.target.checked)
                      }
                    />
                  </div>

                  <div
                    style={{
                      marginTop: 20,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <VATTitle>Use Applet</VATTitle>
                    <SwitchButton
                      checked={field.hasMiniApp}
                      sx={{ m: 1 }}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleAppletToggleChange(index, event.target.checked)
                      }
                    />
                  </div>

                  <div
                    style={{
                      marginTop: 20,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <VATTitle>Allow Cancellation</VATTitle>
                    <SwitchButton
                      checked={!field.isEndSessionDisabled}
                      sx={{ m: 1 }}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleCancellationToggleChange(
                          index,
                          event.target.checked
                        )
                      }
                    />
                  </div>

                  <Tooltip
                    placement='top'
                    title={
                      !vatNumber
                        ? 'Please provide VAT number in order to toggle this field'
                        : ''
                    }
                  >
                    <div
                      style={{
                        marginTop: 20,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <VATTitle>Has VAT</VATTitle>
                      <SwitchButton
                        checked={field.hasVat}
                        disabled={!vatNumber}
                        sx={{ m: 1 }}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => handleVATToggleChange(index, event.target.checked)}
                      />
                    </div>
                  </Tooltip>
                  {field.timeZoneId ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <VATTitle>Time Zone</VATTitle>
                      <VATTitle>{field.timeZoneId}</VATTitle>
                    </div>
                  ) : null}
                </FieldContainer>
              );
            })}
          </FormContainer>
        </FormProvider>
      </ModalContentContainer>
      <Button
        form='customer-form'
        sx={{
          margin: '10px 0',
          width: '90%',
          marginLeft: '5%',
        }}
        type='submit'
        variant='contained'
      >
        {editing ? 'Save Changes' : 'Save New Customer'}
      </Button>
    </Box>
  );
}

export default CustomerFormModal;
